import { addDaysLocal, localToUtc, todayLocal } from '@fos/shared/time';
import { Clock } from 'lucide-react';
import { useEffect, useState } from 'react';
import { errorMessage, validationIssues } from '../../../lib/api';
import { Button } from '../../../ui/Button';
import { DateInput, normalizeTime, TimeInput } from '../../../ui/DateTimeInput';
import { Dialog } from '../../../ui/Dialog';
import { Field } from '../../../ui/Field';
import { Menu, MenuContent, MenuItem, MenuSeparator, MenuTrigger } from '../../../ui/Menu';
import { toast } from '../../../ui/Toast';
import { useInboxActions } from '../hooks';
import { snoozedUntilLabel, snoozePresets } from '../inbox-format';

/** Okno „Odłóż do…” (data + godzina, Europe/Warsaw). */
export function SnoozeDialog({
  open,
  onOpenChange,
  itemId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemId: string;
}) {
  const { snooze } = useInboxActions();
  const [date, setDate] = useState('');
  const [time, setTime] = useState('08:00');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setDate(addDaysLocal(todayLocal(), 1));
    setTime('08:00');
    setError(null);
  }, [open]);

  const submit = async () => {
    const t = normalizeTime(time);
    if (!date || !t) {
      setError('Podaj datę i godzinę');
      return;
    }
    const until = localToUtc(date, t);
    try {
      await snooze.mutateAsync({ id: itemId, until });
      onOpenChange(false);
      toast.success(`Odłożono ${snoozedUntilLabel(until)}`);
    } catch (err) {
      setError(validationIssues(err)[0]?.message ?? errorMessage(err, 'Nie udało się odłożyć.'));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Odłóż do…"
      description="Wiadomość zniknie z Inboxu i wróci jako nowa w wybranym terminie."
      width="s"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button variant="primary" loading={snooze.isPending} onClick={() => void submit()}>
            Odłóż
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-[1fr_110px] gap-3">
        <Field label="Dzień" error={error}>
          <DateInput value={date} min={todayLocal()} onValueChange={setDate} />
        </Field>
        <Field label="Godzina">
          <TimeInput size="m" value={time} onValueChange={setTime} />
        </Field>
      </div>
    </Dialog>
  );
}

/** Przycisk „Odłóż” z szybkimi terminami i „Wybierz termin…”. */
export function SnoozeMenu({ itemId, disabled }: { itemId: string; disabled?: boolean }) {
  const { snooze } = useInboxActions();
  const [dialogOpen, setDialogOpen] = useState(false);
  const now = new Date();
  return (
    <>
      <Menu>
        <MenuTrigger asChild>
          <Button variant="ghost" icon={Clock} disabled={disabled} loading={snooze.isPending} className="max-md:h-11">
            Odłóż
          </Button>
        </MenuTrigger>
        <MenuContent align="start">
          {snoozePresets(now).map((p) => (
            <MenuItem
              key={p.key}
              onSelect={() =>
                snooze.mutate(
                  { id: itemId, until: p.until },
                  { onSuccess: () => toast.success(`Odłożono ${snoozedUntilLabel(p.until)}`) },
                )
              }
            >
              {p.label}
            </MenuItem>
          ))}
          <MenuSeparator />
          <MenuItem onSelect={() => setDialogOpen(true)}>Wybierz termin…</MenuItem>
        </MenuContent>
      </Menu>
      <SnoozeDialog open={dialogOpen} onOpenChange={setDialogOpen} itemId={itemId} />
    </>
  );
}
